// Hospitality H1 (Sep 2026) — is this storefront session a hotel-referred
// one? Binds a `?host_ref=` token (once) to the cart session on the
// server, then asks the server, keyed by (slug, sessionId).
//
// An ordinary storefront visit makes NO extra request: the context is
// only looked up when this tab has a `host_ref` token, or has already
// bound one for this exact session. A rotated session ("Start new
// order") therefore falls straight back to ordinary ordering. The locked
// destination itself is enforced by the server at checkout; what this
// hook returns is only for display.
import { useEffect, useState } from "react";
import { bindHostReferral, fetchHostReferralContext, isHostDestinationContext } from "../../lib/hostReferral";

const boundKey = (slug) => `host_ref_session:${slug}`;

function stripHostRefFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("host_ref")) return;
    url.searchParams.delete("host_ref");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch (_e) { /* noop */ }
}

function readBoundSession(slug) {
  try { return sessionStorage.getItem(boundKey(slug)); } catch (_e) { return null; }
}

function writeBoundSession(slug, sessionId) {
  try {
    if (sessionId) sessionStorage.setItem(boundKey(slug), sessionId);
    else sessionStorage.removeItem(boundKey(slug));
  } catch (_e) { /* noop */ }
}

export default function useHostReferral({ slug, sessionId, token }) {
  const [context, setContext] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!slug || !sessionId) return undefined;
    if (!token && readBoundSession(slug) !== sessionId) {
      setContext(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (token) {
        try {
          const bound = await bindHostReferral(slug, token, sessionId);
          if (isHostDestinationContext(bound)) writeBoundSession(slug, sessionId);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (!cancelled) setError((detail && detail.message) || "This hotel ordering link could not be used.");
        } finally {
          stripHostRefFromUrl();
        }
      }
      try {
        const ctx = await fetchHostReferralContext(slug, sessionId);
        const valid = isHostDestinationContext(ctx) ? ctx : null;
        if (!valid) writeBoundSession(slug, null);
        if (!cancelled) setContext(valid);
      } catch (_e) {
        if (!cancelled) setContext(null);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, sessionId, token]);

  return { context, error };
}
